import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import {
  availableOperators,
  customRuleMapping,
  type ObjectType,
  type RuleDocument,
  type RuleGroupDocument,
  type RuleOptions,
  ValueTypes,
} from '@onevesthq/ov-conditions';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Selector } from '@ovComponents/2-component/selector/selector';
import { UserContext } from '@providers/userContextProvider';
import { Box, Button, Typography, useTheme } from 'hephaestus/web';
import { Add, Clear } from 'hephaestus/webIcons';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Rule } from './components/rule';

export const conditionGraphql = `
  condition {
    ruleGroup {
      combinator
      rules {
        combinator
        field
        operator
        value
        customFieldKey
        customFieldType
        objectType
        rules {
          combinator
          field
          operator
          value
          customFieldKey
          customFieldType
          objectType
          rules {
            combinator
            field
            operator
            value
            customFieldKey
            customFieldType
            objectType
          }
        }
      }
    }
  }
`;

export const FETCH_CUSTOM_FIELDS = gql`
  query fetchCustomFields($input: FetchCustomFieldsInput!) {
    fetchCustomFields(input: $input) {
      customFields{
        id
        key
        type
        format
        objectType
        workflowStep
        translatedName{ ${TRANSLATED_STRING_FIELDS} }
        selectOptions{
          value
          displayText { ${TRANSLATED_STRING_FIELDS} }
        }
        translatedDescription { ${TRANSLATED_STRING_FIELDS} }
      }
    }
  }
`;

const MAX_DEPTH = 3;

export const RuleBuilder = ({
  ruleGroup,
  setRuleGroup,
  objectType,
  removeRuleGroup,
  depth = 0,
}: {
  ruleGroup: RuleGroupDocument;
  setRuleGroup: (r: RuleGroupDocument) => void;
  objectType: ObjectType;
  removeRuleGroup?: () => void;
  depth?: number;
}) => {
  const { t } = useTranslation('components');
  const theme = useTheme();
  const { activeOrganization } = useContext(UserContext);
  const [localRuleGroup, setLocalRuleGroup] =
    useState<RuleGroupDocument>(ruleGroup);

  const { data } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id ?? undefined,
        },
        pagination: {
          offSet: 0,
          perPage: 1000,
        },
      },
    },
  });

  useEffect(() => {
    setLocalRuleGroup(ruleGroup);
  }, [ruleGroup]);

  /**
   * Derived rather than held in state: an effect would leave a painted frame
   * where the query has resolved but the rules have not been recomputed yet.
   */
  const availableRules: RuleOptions[] = useMemo(
    () =>
      data && objectType
        ? customRuleMapping(objectType, data.fetchCustomFields.customFields)
        : [],
    [objectType, data],
  );

  /**
   * Seeded from `availableRules` so an added rule matches one built by picking a
   * field in the dropdown. Object types without built-in rules, such as
   * CLIENT_GROUP, only ever offer custom fields
   */
  const addRule = () => {
    const firstRule = availableRules[0];
    if (!firstRule) return;

    setRuleGroup({
      ...localRuleGroup,
      rules: [
        ...localRuleGroup.rules,
        {
          field: firstRule.name,
          operator: availableOperators(
            firstRule.valueType || ValueTypes.TEXT,
          )[0],
          value: '',
          customFieldKey: firstRule.customField?.key,
          customFieldType: firstRule.valueType,
          objectType,
        },
      ],
    });
  };

  const removeRule = (index: number) => {
    const rs = [...localRuleGroup.rules];
    rs.splice(index, 1);
    setRuleGroup({ ...localRuleGroup, rules: rs });
  };

  const addRuleGroup = () => {
    const newRules = [...localRuleGroup.rules];
    newRules.push({
      combinator: 'and',
      rules: [],
    });
    setRuleGroup({ ...localRuleGroup, rules: newRules });
  };

  if (!localRuleGroup) {
    return null;
  }

  /**
   * Object types such as CLIENT_GROUP have no built-in rules, so custom fields
   * are the only source of conditions. Keyed off `data` rather than `loading`
   * so a still-loading or failed query never claims that none exist.
   */
  const hasNoAvailableRules = !!data && availableRules.length === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
          }}
        >
          <Selector
            options={[
              { label: t('ruleBuilder.and'), type: 'and' },
              { label: t('ruleBuilder.or'), type: 'or' },
            ]}
            selectedOptionType={localRuleGroup.combinator}
            onChange={(event) => {
              setRuleGroup({
                ...localRuleGroup,
                combinator: event.target.value,
              });
            }}
          />
          <Button
            startIcon={<Add />}
            variant='text'
            size='small'
            onClick={addRule}
            disabled={availableRules.length === 0}
          >
            {t('ruleBuilder.addRule')}
          </Button>
          {depth < MAX_DEPTH && (
            <Button
              startIcon={<Add />}
              variant='text'
              size='small'
              onClick={addRuleGroup}
            >
              {t('ruleBuilder.addGroup')}
            </Button>
          )}
        </Box>
        {removeRuleGroup && (
          <Button
            onClick={removeRuleGroup}
            variant='text'
            startIcon={<Clear />}
            size='small'
          >
            {t('ruleBuilder.removeRuleGroup')}
          </Button>
        )}
      </Box>
      {hasNoAvailableRules && (
        <Typography
          variant='bodySmall'
          sx={{ color: theme.color.onSurfaceVariant }}
        >
          {t('ruleBuilder.noAvailableRules')}
        </Typography>
      )}
      {(localRuleGroup.rules || []).length > 0 &&
        (localRuleGroup.rules || []).map((rule, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
            }}
          >
            {'field' in rule && rule.field && (
              <Rule
                rule={rule}
                updateRule={(r: RuleDocument) => {
                  const rs = [...localRuleGroup.rules];
                  rs[index] = r;
                  setRuleGroup({ ...localRuleGroup, rules: rs });
                }}
                availableRules={availableRules}
                objectType={objectType}
                removeRule={() => removeRule(index)}
              />
            )}
            {'rules' in rule && rule.rules && (
              <Card variant='outlined' sx={{ width: '100%' }}>
                <CardContent>
                  <RuleBuilder
                    ruleGroup={rule}
                    objectType={objectType}
                    removeRuleGroup={() => removeRule(index)}
                    setRuleGroup={(r: RuleGroupDocument) => {
                      const rs = [...localRuleGroup.rules];
                      rs[index] = r;
                      setRuleGroup({ ...localRuleGroup, rules: rs });
                    }}
                    depth={depth + 1}
                  />
                </CardContent>
              </Card>
            )}
          </Box>
        ))}
    </Box>
  );
};
